/******* Angular Resourse *******/
import { Injectable, Inject } from "@angular/core";
import { DOCUMENT } from '@angular/common';
import { Router, RoutesRecognized } from "@angular/router";

/******* Core Resourse *******/
import { PrivilegeService } from "./privilege.service";
import { ClientToServerEvents, MessageData, MessageType, ServerToClientEvents } from "@core/types";
import { AccessTokenService } from "./access-token.service";

/******* Plug-In Resourse *******/
import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client';
import { filter, take } from "rxjs/operators";
import { Observable, Subject } from "rxjs";

/******* Environment Resourse *******/
import { environment } from 'environments/environment';

@Injectable({
    providedIn: 'root'
})
export class SocketService {

    // Private
    private _socketUrl: string = environment.socketUrl;
    private _socketOptions: Partial<ManagerOptions & SocketOptions> = {} as Partial<ManagerOptions & SocketOptions>;
    private _socket: Socket<ServerToClientEvents, ClientToServerEvents>;
    private _socketMessageSubject$: Subject<MessageData>;
    private _socketIo: any = io;

    /**
     * Constructor
     * 
     * @param {Router} _router 
     * @param {PrivilegeService} _privilegeService 
     * @param {AccessTokenService} _accessTokenService
     */
    constructor(private _router: Router,
        private _privilegeService: PrivilegeService,
        private _accessTokenService: AccessTokenService,
        @Inject(DOCUMENT) private readonly _document: Document) {

        // initial socket message subject
        this._socketMessageSubject$ = new Subject<MessageData>();

        // Check Access Token initial socket connection 
        this._router.events.pipe(filter(event => event instanceof RoutesRecognized), take(1)).subscribe((e: RoutesRecognized) => {
            const url = e.urlAfterRedirects;
            if (url.indexOf("/pages/") >= 0) this.initialSocketConnection();
        });
    }

    // Accessor
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get socket connected status
     */
    get isSocketFunctional() {
        return this._socket.connected
    }

    // Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get specific event data flow
     * 
     * @param {MessageType} eventName 
     * @returns {Observable<MessageData>}
     */
    public eventFlow(eventName: MessageType): Observable<MessageData> {
        /**
         * Device => real time device list
         * Device Alarm => real time analysis page
         */
        return eventName === MessageType.ALL ?
            this._socketMessageSubject$.asObservable() :
            this._socketMessageSubject$.asObservable().pipe(filter(x => x.eventName === eventName));
    }

    /**
     * Initial socket connection
     */
    public initialSocketConnection(): void {
        if (this._socket) return;
        this._privilegeService.currentUser.pipe(filter(x => x != null), take(1)).subscribe(() => {
            let urlString = this._socketUrl;
            if (urlString.indexOf('http://') !== 0 && urlString.indexOf('https://') !== 0) {
                urlString = this._document.location.origin + this._socketUrl;
            }
            const parseUrl = new URL(urlString);
            this._socketOptions = {
                reconnectionDelayMax: 50000,
                reconnection: true, // dont do auto reconnect
                transports: ['websocket', 'flashsocket', 'htmlfile', 'xhr-polling', 'jsonp-polling', 'polling'],
                path: `${parseUrl.pathname}/${this._privilegeService.isSuperAdminAccess ? "" : ""}socket.io` // super-admin/
            };
            this._socket = this._socketIo(parseUrl.origin, this._socketOptions); // inital socket setting

            //// Socket on connect
            this._socket.on('connect', () => {
                console.log(`Socket Connecting ...`);
            });

            //// Socket on all receiving data
            this._socket.onAny((...args) => {
                let tempMessage: MessageData = {
                    eventName: args[0],
                    type: args[0]
                };
                if (args[1]) tempMessage.data = args[1];
                this._socketMessageSubject$.next(tempMessage);
            });

            //// Socket on connect-error
            this._socket.on('connect_error', (err) => {
                if (err.message === "jwt expired") {
                    if (!this._accessTokenService.refreshFlag) {
                        this._accessTokenService.refreshFlag = true;
                        this._accessTokenService.clearAllInfo();
                        this._accessTokenService.refreshAccessToken().subscribe((token) => {
                            this._accessTokenService.refreshFlag = false;
                            this._accessTokenService.setToken(token.data);
                        }, (error) => {
                            this._accessTokenService.refreshFlag = false;
                            console.log(`Socket Connect Error ... ${error}`);
                        });
                    }

                    this._accessTokenService.currentAccessToken.pipe(
                        filter(token => token !== null),
                        take(1)).subscribe(() => {
                            if (this._socket) {
                                this._socket.connect();
                                console.log(`Socket Update Auth ...`);
                            }
                        });
                }
                else
                    console.log(`Socket Connect Error ... ${err.message}`);
            });

            //// Socket on disconnects
            this._socket.on('disconnect', () => {
                console.log(`Socket Disconnected ...`);
            });

            //// TEST 
            // this.testSocketFunction();

        });
    }

    /**
     * Disconnect the socket
     */
    public disconnectSocket(): void {
        if (this._socket) {
            this._socket.disconnect();
            this._socket.removeAllListeners();
            this._socket.close();
        }
        this._socket = null;
    }

    /**
     * Room Action
     * 
     * @param {'join' | 'leave'} action
     * @param {string} room
     */
    public roomAction(action: 'join' | 'leave', room: string): void {
        if (this._socket) {
            this._socket.emit("request",
                { action, room },
                (response) => {
                    console.log(response); // Handle the response from the server
                });
        }
    }

    /**
     * Test Socket Function
     */
    // public testSocketFunction() {
    //     let tempMessage: MessageData = {
    //         eventName: MessageType.DEVICE_CONNECTION,
    //         type: MessageType.DEVICE_CONNECTION,
    //         data: {
    //             "CustID": "MKTDEMO",
    //             "MAC": "60034752BA64",
    //             "connected": true
    //         }
    //     };
    //     setInterval(() => {
    //         this._socketMessageSubject$.next(tempMessage);
    //     }, 2000);
    // }
}
